/* Teams & Projects — iter-26 admin panel.
 *
 * Three columns:
 *   Left  : project/team list (active + inactive) + Add Project button
 *   Mid   : selected team — header (toggle active), member dropdown + Add Member,
 *           assigned projects list (add/remove)
 *   Right : selected member detail — permission toggles, suspend, elevate to
 *           leader, delete, token allocation form
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, Trash2, ChevronRight, UserPlus, ShieldCheck, Crown, Pause, Play,
  Coins, X, Save, Loader2, Folder, Power, Users as UsersIcon,
} from 'lucide-react';
import { PanelHeader } from './_shared';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const PERM_LIST = [
  { key: 'read',            label: 'Read' },
  { key: 'write',           label: 'Write / edit chapters' },
  { key: 'export',          label: 'Export' },
  { key: 'delete_chapters', label: 'Delete chapters' },
  { key: 'run_ai',          label: 'Run AI generation' },
  { key: 'run_plagiarism',  label: 'Run plagiarism scan' },
];

function Toggle({ checked, onChange, testid, label }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 text-[12px]">
      <span className="text-foreground/85">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-4.5 w-8 shrink-0 items-center rounded-full transition ${
          checked ? 'bg-primary' : 'bg-secondary'
        }`}
        style={{ height: '18px', width: '32px' }}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card transition ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function AddTeamRow({ onAdded }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await adminApi.createTeam(name.trim());
      toast.success(`Team "${r.team.name}" created.`);
      setName('');
      onAdded(r.team);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-1.5">
      <input
        type="text" required value={name} onChange={(e) => setName(e.target.value)}
        placeholder="New project / team name"
        data-testid="teams-create-name"
        className="h-7 flex-1 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
      />
      <button type="submit" disabled={busy} data-testid="teams-create-submit"
              className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
      </button>
    </form>
  );
}

function MemberDetail({ team, member, allUsers, onChanged, onClose }) {
  const [busy, setBusy] = useState(false);
  const u = member?.user || allUsers.find((x) => x.user_id === member.user_id) || {};
  const update = async (patch) => {
    setBusy(true);
    try {
      await adminApi.updateMember(team.id, member.id, patch);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const togglePerm = (k, v) => update({ permissions: { [k]: v } });
  const elevate = () => update({ role: member.role === 'leader' ? 'member' : 'leader' });
  const suspend = () => update({ suspended: !member.suspended });
  const distrib = () => update({ can_distribute_tokens: !member.can_distribute_tokens });
  const remove = async () => {
    if (!(await openConfirm({
      title: `Remove "${u.name || u.email}" from "${team.name}"?`,
      tone: 'destructive',
    }))) return;
    try {
      await adminApi.removeMember(team.id, member.id);
      toast.success('Member removed.');
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  const [tokens, setTokens] = useState('');
  const [note, setNote] = useState('');
  const allocate = async () => {
    const n = parseInt(tokens, 10);
    if (!n || n <= 0) return toast.error('Enter a positive integer.');
    setBusy(true);
    try {
      const r = await adminApi.allocateTokens(team.id, member.id, n, note || '');
      toast.success(`Allocated ${n.toLocaleString()} tokens (source: ${r.source}).`);
      setTokens(''); setNote('');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Allocation failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`team-member-detail-${member.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
            {member.role === 'leader' ? 'Team Leader' : 'Team Member'}{member.suspended ? ' · SUSPENDED' : ''}
          </div>
          <div className="truncate text-[14px] font-semibold">{u.name || u.email || member.user_id}</div>
          <div className="truncate text-[11px] text-muted-foreground">{u.email}</div>
        </div>
        <button onClick={onClose} className="rounded-sm p-1 text-muted-foreground hover:bg-secondary"><X className="h-3.5 w-3.5" /></button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px] text-muted-foreground">
        <div>User wallet</div><div className="text-right font-mono-stat text-foreground">{(u.tokens_purchased ?? 0).toLocaleString()}</div>
        <div>Sub-allocation</div><div className="text-right font-mono-stat text-foreground">{(member.allocated_tokens ?? 0).toLocaleString()}</div>
      </div>

      <div className="mt-4 border-t border-border pt-3">
        <div className="mb-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Permissions</div>
        {PERM_LIST.map((p) => (
          <Toggle
            key={p.key}
            label={p.label}
            testid={`team-member-perm-${p.key}`}
            checked={!!member.permissions?.[p.key]}
            onChange={(v) => togglePerm(p.key, v)}
          />
        ))}
      </div>

      {member.role === 'leader' && (
        <div className="mt-3 border-t border-border pt-3">
          <Toggle
            label="Can distribute tokens to this team"
            testid="team-member-can-distribute"
            checked={!!member.can_distribute_tokens}
            onChange={() => distrib()}
          />
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3 text-[11.5px]">
        <button onClick={elevate} data-testid="team-member-toggle-role"
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 hover:bg-secondary/80 disabled:opacity-60">
          <Crown className="h-3 w-3" />
          {member.role === 'leader' ? 'Demote to member' : 'Elevate to leader'}
        </button>
        <button onClick={suspend} data-testid="team-member-toggle-suspend"
                disabled={busy}
                className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 ${member.suspended ? 'bg-success/15 text-success hover:bg-success/25' : 'bg-warning/15 text-warning hover:bg-warning/25'}`}>
          {member.suspended ? <><Play className="h-3 w-3" /> Reinstate</> : <><Pause className="h-3 w-3" /> Suspend</>}
        </button>
        <button onClick={remove} data-testid="team-member-remove"
                className="ml-auto inline-flex items-center gap-1 rounded-sm bg-destructive/15 px-2 py-1 text-destructive hover:bg-destructive/25">
          <Trash2 className="h-3 w-3" /> Remove
        </button>
      </div>

      <div className="mt-3 rounded-sm border border-dashed border-border p-3" data-testid="team-member-allocate">
        <div className="mb-1 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
          <Coins className="h-3 w-3 text-primary" /> Allocate tokens
        </div>
        <div className="flex flex-wrap gap-1.5">
          <input type="number" value={tokens} onChange={(e) => setTokens(e.target.value)}
                  placeholder="Token count" min={1} step={1}
                  data-testid="team-member-allocate-tokens"
                  className="h-7 w-32 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary" />
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Note (optional)" maxLength={200}
                  data-testid="team-member-allocate-note"
                  className="h-7 flex-1 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary" />
          <button onClick={allocate} disabled={busy} data-testid="team-member-allocate-submit"
                  className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Allocate
          </button>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">
          Source resolves to the team leader&apos;s wallet (if a leader with &ldquo;Can distribute tokens&rdquo; exists),
          otherwise the admin pool.
        </p>
      </div>
    </div>
  );
}

export default function TeamsPanel() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selTeamId, setSelTeamId] = useState(null);
  const [members, setMembers] = useState([]);
  const [selMemberId, setSelMemberId] = useState(null);
  const [addUserId, setAddUserId] = useState('');
  const [addProjectId, setAddProjectId] = useState('');

  const team   = useMemo(() => teams.find((t) => t.id === selTeamId) || null, [teams, selTeamId]);
  const member = useMemo(() => members.find((m) => m.id === selMemberId) || null, [members, selMemberId]);

  const refreshTeams = useCallback(async () => {
    const r = await adminApi.listTeams();
    setTeams(r.teams || []);
    return r.teams || [];
  }, []);

  const refreshMembers = useCallback(async (tid) => {
    if (!tid) { setMembers([]); return; }
    const r = await adminApi.listMembers(tid);
    setMembers(r.members || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [t, u, p] = await Promise.all([
          adminApi.listTeams(),
          adminApi.users(),
          adminApi.listAllProjects(),
        ]);
        setTeams(t.teams || []);
        setUsers(u.users || []);
        setProjects(p.projects || []);
        if ((t.teams || []).length > 0) setSelTeamId(t.teams[0].id);
      } catch (e) { toast.error('Could not load teams.'); }
    })();
  }, []);

  useEffect(() => { refreshMembers(selTeamId); setSelMemberId(null); }, [selTeamId, refreshMembers]);

  const toggleActive = async () => {
    if (!team) return;
    await adminApi.updateTeam(team.id, { active: !team.active });
    toast.success(`Team ${team.active ? 'deactivated' : 'activated'}.`);
    refreshTeams();
  };

  const removeTeam = async () => {
    if (!team) return;
    if (!(await openConfirm({
      title: `Delete team "${team.name}" and all its memberships?`,
      confirmLabel: 'Delete team',
      tone: 'destructive',
    }))) return;
    await adminApi.deleteTeam(team.id);
    toast.success('Team deleted.');
    const remaining = await refreshTeams();
    setSelTeamId(remaining[0]?.id || null);
  };

  const addMember = async () => {
    if (!team || !addUserId) return;
    try {
      await adminApi.addMember(team.id, addUserId);
      setAddUserId('');
      refreshMembers(team.id);
      refreshTeams();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  const assignProj = async () => {
    if (!team || !addProjectId) return;
    try {
      await adminApi.assignProject(team.id, addProjectId);
      setAddProjectId('');
      refreshTeams();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
  };

  const unassignProj = async (pid) => {
    if (!team) return;
    await adminApi.unassignProject(team.id, pid);
    refreshTeams();
  };

  // Users not already members of the selected team
  const candidateUsers = useMemo(() => {
    if (!team) return users;
    const memberIds = new Set(members.map((m) => m.user_id));
    return users.filter((u) => !memberIds.has(u.user_id));
  }, [users, members, team]);

  // Projects assigned vs available
  const assignedProjects = useMemo(() => {
    if (!team) return [];
    const idSet = new Set(team.project_ids || []);
    return projects.filter((p) => idSet.has(p.id));
  }, [team, projects]);
  const availableProjects = useMemo(() => {
    if (!team) return projects;
    const idSet = new Set(team.project_ids || []);
    return projects.filter((p) => !idSet.has(p.id));
  }, [team, projects]);

  return (
    <section data-testid="teams-panel">
      <PanelHeader title="Teams & Projects"
                    subtitle="Group users into project teams, assign The Loom projects, manage per-member permissions, and let leaders allocate token sub-budgets to their members."
                    onRefresh={async () => { await refreshTeams(); refreshMembers(selTeamId); }} />
      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[260px_1fr_360px]">
        {/* Left — team list */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Projects / Teams</div>
            <div className="text-[10px] text-muted-foreground">{teams.length}</div>
          </div>
          <AddTeamRow onAdded={(t) => { setTeams((arr) => [t, ...arr]); setSelTeamId(t.id); }} />
          <div className="mt-3 max-h-[480px] space-y-1 overflow-y-auto">
            {teams.length === 0 && <div className="text-[11.5px] text-muted-foreground">No teams yet.</div>}
            {teams.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelTeamId(t.id)}
                data-testid={`team-row-${t.id}`}
                className={`group flex w-full items-center gap-2 rounded-sm border px-2 py-1.5 text-left text-[12px] transition ${
                  selTeamId === t.id ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-secondary'
                }`}
              >
                <Folder className={`h-3.5 w-3.5 ${t.active ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="min-w-0 flex-1">
                  <div className={`truncate font-semibold ${t.active ? '' : 'text-muted-foreground line-through'}`}>{t.name}</div>
                  <div className="text-[10px] text-muted-foreground">{t.member_count} member{t.member_count === 1 ? '' : 's'} · {(t.project_ids || []).length} project{(t.project_ids || []).length === 1 ? '' : 's'}</div>
                </div>
                <ChevronRight className="h-3 w-3 opacity-0 transition group-hover:opacity-100" />
              </button>
            ))}
          </div>
        </div>

        {/* Mid — team detail */}
        {!team ? (
          <div className="rounded-md border border-dashed border-border bg-card p-8 text-center text-[12px] text-muted-foreground">
            Select a team to manage members and project assignments.
          </div>
        ) : (
          <div className="rounded-md border border-border bg-card p-4" data-testid={`team-detail-${team.id}`}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Team</div>
                <div className="text-[16px] font-semibold tracking-tight">{team.name}</div>
              </div>
              <button onClick={toggleActive} data-testid="team-toggle-active"
                      className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11.5px] font-semibold ${team.active ? 'bg-success/15 text-success hover:bg-success/25' : 'bg-warning/15 text-warning hover:bg-warning/25'}`}>
                <Power className="h-3 w-3" /> {team.active ? 'Active' : 'Inactive'}
              </button>
              <button onClick={removeTeam} data-testid="team-delete"
                      className="rounded-sm bg-destructive/15 p-1.5 text-destructive hover:bg-destructive/25">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>

            {/* Members */}
            <div className="mt-4 border-t border-border pt-3">
              <div className="mb-1 flex items-center justify-between">
                <div className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
                  <UsersIcon className="h-3 w-3" /> Members ({members.length})
                </div>
              </div>
              <div className="mb-2 flex gap-1.5">
                <select value={addUserId} onChange={(e) => setAddUserId(e.target.value)}
                        data-testid="team-add-member-select"
                        className="h-7 flex-1 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary">
                  <option value="">Select a user…</option>
                  {candidateUsers.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {(u.name || u.email || u.user_id).slice(0, 36)}{u.email ? ` (${u.email.slice(0, 32)})` : ''}
                    </option>
                  ))}
                </select>
                <button onClick={addMember} disabled={!addUserId} data-testid="team-add-member-submit"
                        className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                  <UserPlus className="h-3 w-3" /> Add
                </button>
              </div>
              <div className="space-y-1">
                {members.length === 0 && <div className="text-[11.5px] text-muted-foreground">No members yet — pick a user above.</div>}
                {members.map((m) => {
                  const u = m.user || {};
                  return (
                    <button
                      key={m.id}
                      onClick={() => setSelMemberId(m.id)}
                      data-testid={`team-member-row-${m.id}`}
                      className={`flex w-full items-center gap-2 rounded-sm border px-2 py-1.5 text-left text-[11.5px] transition ${
                        selMemberId === m.id ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-secondary'
                      }`}
                    >
                      <div className={`flex h-6 w-6 items-center justify-center rounded-sm ${m.role === 'leader' ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                        {m.role === 'leader' ? <Crown className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`truncate font-semibold ${m.suspended ? 'text-muted-foreground line-through' : ''}`}>{u.name || u.email || m.user_id}</div>
                        <div className="truncate text-[10px] text-muted-foreground">{u.email || ''} {m.role === 'leader' && m.can_distribute_tokens && '· can distribute tokens'}</div>
                      </div>
                      <div className="text-right text-[10px] text-muted-foreground">
                        {Object.entries(m.permissions || {}).filter(([, v]) => v).length}/6 perms · {(m.allocated_tokens || 0).toLocaleString()} alloc
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Projects */}
            <div className="mt-4 border-t border-border pt-3">
              <div className="mb-1 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
                <Folder className="h-3 w-3" /> Assigned The Loom projects ({(team.project_ids || []).length})
              </div>
              <div className="mb-2 flex gap-1.5">
                <select value={addProjectId} onChange={(e) => setAddProjectId(e.target.value)}
                        data-testid="team-add-project-select"
                        className="h-7 flex-1 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary">
                  <option value="">Pick a project…</option>
                  {availableProjects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button onClick={assignProj} disabled={!addProjectId} data-testid="team-add-project-submit"
                        className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                  <Plus className="h-3 w-3" /> Assign
                </button>
              </div>
              <div className="space-y-1">
                {assignedProjects.length === 0 && <div className="text-[11.5px] text-muted-foreground">No projects assigned yet.</div>}
                {assignedProjects.map((p) => (
                  <div key={p.id} data-testid={`team-project-${p.id}`}
                        className="flex items-center justify-between rounded-sm border border-border bg-background px-2 py-1.5 text-[11.5px]">
                    <div className="truncate"><Folder className="mr-1 inline h-3 w-3 text-primary" /> {p.name}</div>
                    <button onClick={() => unassignProj(p.id)} className="rounded-sm p-0.5 text-muted-foreground hover:bg-secondary hover:text-warning">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Right — member detail */}
        {member ? (
          <MemberDetail
            team={team}
            member={member}
            allUsers={users}
            onChanged={() => refreshMembers(team.id)}
            onClose={() => setSelMemberId(null)}
          />
        ) : (
          <div className="rounded-md border border-dashed border-border bg-card p-8 text-center text-[12px] text-muted-foreground">
            Pick a member from the middle column to manage their permissions and token allocation.
          </div>
        )}
      </div>
    </section>
  );
}
